import { useState, useMemo } from "react"
import { format } from "date-fns"
import { es } from "date-fns/locale"
import * as XLSX from "xlsx"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Search, Download, Users, Trophy, CalendarIcon, Filter, BarChart3, X } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { useAllGames } from "@/graphql/hooks/useGames"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import { useTournamentTypesListPlain } from "@/graphql/hooks/useTournamentTypesList"
import { useStoreList } from "@/graphql/hooks/useStoreList"
import { useAllFormats } from "@/graphql/hooks/useAllFormats"
import {
  useReportUserList,
  useReportStoreTournaments,
  type ReportFilters,
  type ReportPerson,
  type ReportTournament,
} from "@/graphql/hooks/useReports"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts"

const ITEMS_PER_PAGE = 20

// Document type mapping
const documentTypeMap: Record<number, string> = {
  1: "RUT",
  2: "DNI",
  3: "Pasaporte",
  4: "Cédula",
}

// Chart colors - 30+ color palette for charts
const CHART_COLORS = [
  "#6366f1", // Indigo
  "#22c55e", // Green
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#3b82f6", // Blue
  "#8b5cf6", // Violet
  "#ec4899", // Pink
  "#14b8a6", // Teal
  "#f97316", // Orange
  "#84cc16", // Lime
  "#06b6d4", // Cyan
  "#a855f7", // Purple
  "#10b981", // Emerald
  "#eab308", // Yellow
  "#0ea5e9", // Sky
  "#d946ef", // Fuchsia
  "#64748b", // Slate
  "#78716c", // Stone
  "#f43f5e", // Rose
  "#059669", // Emerald darker
  "#7c3aed", // Violet darker
  "#db2777", // Pink darker
  "#0891b2", // Cyan darker
  "#ca8a04", // Yellow darker
  "#dc2626", // Red darker
  "#2563eb", // Blue darker
  "#16a34a", // Green darker
  "#c026d3", // Fuchsia darker
  "#0d9488", // Teal darker
  "#ea580c", // Orange darker
  "#4f46e5", // Indigo darker
  "#65a30d", // Lime darker
]

export function ReportsManagement() {
  const { toast } = useToast()
  const [activeReport, setActiveReport] = useState("users")
  const [showDashboard, setShowDashboard] = useState(false)
  const [trendGranularity, setTrendGranularity] = useState<"day" | "week" | "month">("day")

  // Filter states
  const [selectedGames, setSelectedGames] = useState<number[]>([])
  const [dateStart, setDateStart] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() - 7)
    return date.toISOString().split("T")[0]
  })
  const [dateEnd, setDateEnd] = useState(() => new Date().toISOString().split("T")[0])
  const [selectedCountry, setSelectedCountry] = useState<number>(0)
  const [selectedRegion, setSelectedRegion] = useState<number>(0)
  const [selectedLocation, setSelectedLocation] = useState<number>(0)
  const [selectedTournamentTypes, setSelectedTournamentTypes] = useState<number[]>([])
  const [selectedStores, setSelectedStores] = useState<number[]>([])

  // Search filters for results
  const [usersSearchQuery, setUsersSearchQuery] = useState("")
  const [tournamentsSearchQuery, setTournamentsSearchQuery] = useState("")

  // Pagination
  const [usersPage, setUsersPage] = useState(1)
  const [tournamentsPage, setTournamentsPage] = useState(1)

  // Data hooks
  const { games, gamesMap } = useAllGames(true)
  const { countries, countriesMap } = useCountries()
  const { regions, regionsMap } = useRegions(selectedCountry)
  const { locations, refetchLocations } = useLocations({ regionId: selectedRegion })
  const { types: tournamentTypes } = useTournamentTypesListPlain()
  const { formats } = useAllFormats()
  const { stores } = useStoreList({ filter: { limit: 500, active: 1 } })

  // Report hooks
  const { executeReport: executeUserReport, persons, loading: usersLoading } = useReportUserList()
  const {
    executeReport: executeTournamentReport,
    tournaments,
    loading: tournamentsLoading,
  } = useReportStoreTournaments()

  // Create lookup maps for display names
  const storesMap = useMemo(() => {
    return stores.reduce((acc, store) => {
      acc.set(store.id, store.name)
      return acc
    }, new Map<number, string>())
  }, [stores])

  const tournamentTypesMap = useMemo(() => {
    return tournamentTypes.reduce((acc, type) => {
      acc.set(type.id, type.name)
      return acc
    }, new Map<number, string>())
  }, [tournamentTypes])

  const formatsMap = useMemo(() => {
    return formats.reduce((acc, format) => {
      acc.set(format.id, format.name)
      return acc
    }, new Map<number, string>())
  }, [formats])

  // Handle region change to refetch locations
  const handleRegionChange = (value: string) => {
    const regionId = parseInt(value)
    setSelectedRegion(regionId)
    setSelectedLocation(0)
    if (regionId) {
      refetchLocations(regionId)
    }
  }

  // Build filters object
  const buildFilters = (): ReportFilters => ({
    gamesIds: selectedGames,
    dates: {
      start: new Date(dateStart).toISOString(),
      end: new Date(dateEnd).toISOString(),
    },
    location: {
      countryId: selectedCountry,
      regionId: selectedRegion,
      locationId: selectedLocation,
    },
    tournamentTypesIds: selectedTournamentTypes,
    storesIds: selectedStores,
    limit: 99999,
  })

  // Execute search
  const handleSearch = async () => {
    const filters = buildFilters()
    try {
      if (activeReport === "users") {
        setUsersPage(1)
        setUsersSearchQuery("")
        await executeUserReport(filters)
      } else {
        setTournamentsPage(1)
        setTournamentsSearchQuery("")
        await executeTournamentReport(filters)
      }
    } catch {
      toast({
        title: "Error",
        description: "No se pudo ejecutar el reporte",
        variant: "destructive",
      })
    }
  }

  // Helper functions for address lookup
  const getCountryName = (countryId?: number): string => {
    if (!countryId) return "-"
    const country = countriesMap.get(countryId)
    return country?.name || "-"
  }

  const getRegionName = (regionId?: number): string => {
    if (!regionId) return "-"
    const region = regionsMap.get(regionId)
    return region?.fullName || region?.name || "-"
  }

  // Filter persons by search query
  const filteredPersons = useMemo(() => {
    if (!usersSearchQuery.trim()) return persons
    const query = usersSearchQuery.toLowerCase()
    return persons.filter((person) => {
      const fullName = `${person.name} ${person.firstSurname} ${person.lastSurname}`.toLowerCase()
      return (
        fullName.includes(query) ||
        person.email?.toLowerCase().includes(query) ||
        person.code?.toLowerCase().includes(query) ||
        person.documentNumber?.toLowerCase().includes(query)
      )
    })
  }, [persons, usersSearchQuery])

  // Filter tournaments by search query
  const filteredTournaments = useMemo(() => {
    if (!tournamentsSearchQuery.trim()) return tournaments
    const query = tournamentsSearchQuery.toLowerCase()
    return tournaments.filter((tournament) => {
      const storeName = storesMap.get(tournament.storeId)?.toLowerCase() || ""
      const gameName = gamesMap.get(tournament.gameId)?.name?.toLowerCase() || ""
      return (
        tournament.name?.toLowerCase().includes(query) ||
        storeName.includes(query) ||
        gameName.includes(query) ||
        tournament.id.toString().includes(query)
      )
    })
  }, [tournaments, tournamentsSearchQuery, storesMap, gamesMap])

  // Dashboard data calculations
  const usersByCountryData = useMemo(() => {
    const countryCount: Record<number, number> = {}
    persons.forEach((person) => {
      const countryId = person.Address?.countryId || 0
      countryCount[countryId] = (countryCount[countryId] || 0) + 1
    })
    return Object.entries(countryCount)
      .map(([id, count]) => ({
        name: getCountryName(parseInt(id)) || "Sin país",
        value: count,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
  }, [persons, countriesMap])

  const usersByEmailValidData = useMemo(() => {
    const valid = persons.filter((p) => p.emailValid).length
    const invalid = persons.length - valid
    return [
      { name: "Email Válido", value: valid },
      { name: "Email Inválido", value: invalid },
    ]
  }, [persons])

  const tournamentsByStoreData = useMemo(() => {
    const storeCount: Record<number, number> = {}
    tournaments.forEach((t) => {
      storeCount[t.storeId] = (storeCount[t.storeId] || 0) + 1
    })
    return Object.entries(storeCount)
      .map(([id, count]) => ({
        name: storesMap.get(parseInt(id)) || `ID: ${id}`,
        value: count,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
  }, [tournaments, storesMap])

  const tournamentsByGameData = useMemo(() => {
    const gameCount: Record<number, number> = {}
    tournaments.forEach((t) => {
      gameCount[t.gameId] = (gameCount[t.gameId] || 0) + 1
    })
    return Object.entries(gameCount)
      .map(([id, count]) => ({
        name: gamesMap.get(parseInt(id))?.name || `ID: ${id}`,
        value: count,
      }))
      .sort((a, b) => b.value - a.value)
  }, [tournaments, gamesMap])

  const tournamentsByStatusData = useMemo(() => {
    const statusCount: Record<number, number> = {}
    tournaments.forEach((t) => {
      statusCount[t.statusId] = (statusCount[t.statusId] || 0) + 1
    })
    const statusNames: Record<number, string> = {
      1: "Programado",
      2: "Inscripciones",
      3: "Iniciado",
      4: "Proceso",
      5: "Terminado",
      6: "Reportado",
    }
    return Object.entries(statusCount).map(([id, count]) => ({
      name: statusNames[parseInt(id)] || `Estado ${id}`,
      value: count,
    }))
  }, [tournaments])

  // Trend data calculations - Users by registration date (using tournaments played as proxy)
  const usersTrendData = useMemo(() => {
    if (persons.length === 0) return []

    // Group users by their tournaments' dates
    const dateMap: Record<string, number> = {}

    persons.forEach((person) => {
      if (person.Tournaments && person.Tournaments.length > 0) {
        // Count unique users per date based on their tournament participation
        const seenDates = new Set<string>()
        person.Tournaments.forEach((tp) => {
          // Use tournament id as we don't have created date - group by tournament
          const tournamentId = tp.Tournament.id
          const dateKey = tournamentId.toString()
          if (!seenDates.has(dateKey)) {
            seenDates.add(dateKey)
          }
        })
      }
    })

    // Since we don't have user registration date, use tournaments' dates to show participation trends
    const tournamentDates: Record<string, Set<number>> = {}

    persons.forEach((person) => {
      if (person.Tournaments) {
        person.Tournaments.forEach((tp) => {
          // We'll aggregate by the date filter range instead since we don't have tournament date
          const key = tp.Tournament.id.toString()
          if (!tournamentDates[key]) {
            tournamentDates[key] = new Set()
          }
          tournamentDates[key].add(person.id)
        })
      }
    })

    // For users, show participation count per tournament (as we don't have dates)
    return Object.entries(tournamentDates)
      .map(([tournamentId, userIds]) => ({
        name: `T-${tournamentId}`,
        usuarios: userIds.size,
      }))
      .slice(0, 20) // Limit to 20 points
  }, [persons])

  // Trend data for tournaments by date
  const tournamentsTrendData = useMemo(() => {
    if (tournaments.length === 0) return []

    const dateFormat = {
      day: "yyyy-MM-dd",
      week: "yyyy-'W'ww",
      month: "yyyy-MM",
    }

    const dateMap: Record<string, number> = {}

    tournaments.forEach((tournament) => {
      if (tournament.startDate) {
        let dateKey: string
        const date = new Date(tournament.startDate)

        if (trendGranularity === "day") {
          dateKey = format(date, dateFormat.day)
        } else if (trendGranularity === "week") {
          dateKey = format(date, dateFormat.week)
        } else {
          dateKey = format(date, dateFormat.month)
        }

        dateMap[dateKey] = (dateMap[dateKey] || 0) + 1
      }
    })

    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        name: date,
        torneos: count,
      }))
  }, [tournaments, trendGranularity])

  // Participants trend (count of players per date)
  const participantsTrendData = useMemo(() => {
    if (tournaments.length === 0) return []

    const dateFormat = {
      day: "yyyy-MM-dd",
      week: "yyyy-'W'ww",
      month: "yyyy-MM",
    }

    const dateMap: Record<string, number> = {}

    tournaments.forEach((tournament) => {
      if (tournament.startDate && tournament.TournamentPlayers) {
        let dateKey: string
        const date = new Date(tournament.startDate)

        if (trendGranularity === "day") {
          dateKey = format(date, dateFormat.day)
        } else if (trendGranularity === "week") {
          dateKey = format(date, dateFormat.week)
        } else {
          dateKey = format(date, dateFormat.month)
        }

        dateMap[dateKey] = (dateMap[dateKey] || 0) + tournament.TournamentPlayers.length
      }
    })

    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({
        name: date,
        participantes: count,
      }))
  }, [tournaments, trendGranularity])

  // Export to Excel
  const exportToExcel = () => {
    try {
      const wb = XLSX.utils.book_new()
      let fileName = ""

      if (activeReport === "users") {
        // Main users sheet
        const usersData = filteredPersons.map((person) => ({
          ID: person.id,
          Código: person.code,
          Nombre: person.name,
          "Primer Apellido": person.firstSurname,
          "Segundo Apellido": person.lastSurname,
          Email: person.email,
          "Email Válido": person.emailValid ? "Sí" : "No",
          "Tipo Documento": documentTypeMap[person.documentNumberType || 0] || "-",
          "Número Documento": person.documentNumber || "-",
          "Torneos Jugados": person.playedTournaments,
          País: getCountryName(person.Address?.countryId),
          Región: getRegionName(person.Address?.regionId),
        }))

        const usersSheet = XLSX.utils.json_to_sheet(usersData)
        XLSX.utils.book_append_sheet(wb, usersSheet, "Usuarios")

        // Players with tournament details sheet
        const playerTournamentsData: Record<string, unknown>[] = []
        filteredPersons.forEach((person) => {
          if (person.Tournaments && person.Tournaments.length > 0) {
            person.Tournaments.forEach((tp) => {
              playerTournamentsData.push({
                "ID Usuario": person.id,
                Código: person.code,
                "Nombre Completo": `${person.name} ${person.firstSurname} ${person.lastSurname}`,
                Email: person.email,
                "Tipo Documento": documentTypeMap[person.documentNumberType || 0] || "-",
                "Número Documento": person.documentNumber || "-",
                País: getCountryName(person.Address?.countryId),
                Región: getRegionName(person.Address?.regionId),
                "ID Torneo": tp.Tournament.id,
                "Nombre Torneo": tp.Tournament.name,
                Tienda: storesMap.get(tp.Tournament.storeId) || `ID: ${tp.Tournament.storeId}`,
                Juego: gamesMap.get(tp.Tournament.gameId)?.name || `ID: ${tp.Tournament.gameId}`,
                Raza: racesMap[tp.raceId] || `Sin raza`,
                Puntos: tp.points,
              })
            })
          }
        })

        const tournamentsSheet = XLSX.utils.json_to_sheet(playerTournamentsData)
        XLSX.utils.book_append_sheet(wb, tournamentsSheet, "Detalle Torneos")

        fileName = `reporte-usuarios-${format(new Date(), "yyyy-MM-dd")}.xlsx`
      } else {
        // Main tournaments sheet
        const tournamentsData = filteredTournaments.map((tournament) => ({
          ID: tournament.id,
          Nombre: tournament.name,
          "Fecha Inicio": tournament.startDate
            ? format(new Date(tournament.startDate), "dd/MM/yyyy", { locale: es })
            : "",
          Tienda: storesMap.get(tournament.storeId) || `ID: ${tournament.storeId}`,
          Juego: gamesMap.get(tournament.gameId)?.name || `ID: ${tournament.gameId}`,
          "Tipo Torneo": tournamentTypesMap.get(tournament.typeId) || `ID: ${tournament.typeId}`,
          Formato: formatsMap.get(tournament.formatId) || `ID: ${tournament.formatId}`,
          Estado:
            tournament.statusId === 4
              ? "Finalizado"
              : tournament.statusId === 3
                ? "En curso"
                : tournament.statusId === 2
                  ? "Inscripciones"
                  : "Pendiente",
          "Cantidad Jugadores": tournament.TournamentPlayers?.length || 0,
        }))

        const tournamentsSheet = XLSX.utils.json_to_sheet(tournamentsData)
        XLSX.utils.book_append_sheet(wb, tournamentsSheet, "Torneos")

        // Participants detail sheet
        const participantsData: Record<string, unknown>[] = []
        filteredTournaments.forEach((tournament) => {
          if (tournament.TournamentPlayers && tournament.TournamentPlayers.length > 0) {
            tournament.TournamentPlayers.forEach((tp) => {
              participantsData.push({
                "ID Torneo": tournament.id,
                "Nombre Torneo": tournament.name,
                "Fecha Torneo": tournament.startDate
                  ? format(new Date(tournament.startDate), "dd/MM/yyyy", { locale: es })
                  : "",
                Tienda: storesMap.get(tournament.storeId) || `ID: ${tournament.storeId}`,
                Juego: gamesMap.get(tournament.gameId)?.name || `ID: ${tournament.gameId}`,
                "Tipo Torneo":
                  tournamentTypesMap.get(tournament.typeId) || `ID: ${tournament.typeId}`,
                Formato: formatsMap.get(tournament.formatId) || `ID: ${tournament.formatId}`,
                "ID Jugador": tp.Person.id,
                "Código Jugador": tp.Person.code,
                "Nombre Jugador": `${tp.Person.name} ${tp.Person.firstSurname} ${tp.Person.lastSurname}`,
                "Email Jugador": tp.Person.email,
                "Email Válido": tp.Person.emailValid ? "Sí" : "No",
                Raza: racesMap[tp.raceId] || `Sin raza`,
                Puntos: tp.points,
              })
            })
          }
        })

        const participantsSheet = XLSX.utils.json_to_sheet(participantsData)
        XLSX.utils.book_append_sheet(wb, participantsSheet, "Participantes")

        fileName = `reporte-torneos-${format(new Date(), "yyyy-MM-dd")}.xlsx`
      }

      XLSX.writeFile(wb, fileName)

      toast({
        title: "Exportación exitosa",
        description: `Se ha descargado el archivo ${fileName}`,
      })
    } catch {
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte",
        variant: "destructive",
      })
    }
  }

  // Paginated data - now using filtered data
  const paginatedUsers = useMemo(() => {
    const start = (usersPage - 1) * ITEMS_PER_PAGE
    return filteredPersons.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredPersons, usersPage])

  const paginatedTournaments = useMemo(() => {
    const start = (tournamentsPage - 1) * ITEMS_PER_PAGE
    return filteredTournaments.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredTournaments, tournamentsPage])

  const totalUsersPages = Math.ceil(filteredPersons.length / ITEMS_PER_PAGE)
  const totalTournamentsPages = Math.ceil(filteredTournaments.length / ITEMS_PER_PAGE)

  // Reset pagination when search query changes
  const handleUsersSearchChange = (value: string) => {
    setUsersSearchQuery(value)
    setUsersPage(1)
  }

  const handleTournamentsSearchChange = (value: string) => {
    setTournamentsSearchQuery(value)
    setTournamentsPage(1)
  }

  // Multi-select handlers
  const toggleGameSelection = (gameId: number) => {
    setSelectedGames((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId],
    )
  }

  const toggleTournamentTypeSelection = (typeId: number) => {
    setSelectedTournamentTypes((prev) =>
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId],
    )
  }

  const handleStoreChange = (value: string) => {
    const storeId = parseInt(value)
    if (storeId === 0) {
      setSelectedStores([])
    } else {
      setSelectedStores((prev) =>
        prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId],
      )
    }
  }

  const removeStore = (storeId: number) => {
    setSelectedStores((prev) => prev.filter((id) => id !== storeId))
  }

  // Check if we have data for dashboard
  const hasUsersData = persons.length > 0
  const hasTournamentsData = tournaments.length > 0

  return (
    <div className="space-y-6">
      <Tabs value={activeReport} onValueChange={setActiveReport}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Usuarios Únicos
          </TabsTrigger>
          <TabsTrigger value="tournaments" className="flex items-center gap-2">
            <Trophy className="h-4 w-4" />
            Torneos por Tiendas
          </TabsTrigger>
        </TabsList>

        {/* Filters Section */}
        <Card className="mt-4">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Filtros de Búsqueda</CardTitle>
            </div>
            <CardDescription>Configure los filtros para generar el reporte</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Fecha Inicio
                </Label>
                <Input
                  type="date"
                  value={dateStart}
                  onChange={(e) => setDateStart(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Fecha Fin
                </Label>
                <Input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
              </div>
            </div>

            {/* Games Multi-select */}
            <div className="space-y-2">
              <Label>Juegos</Label>
              <div className="flex flex-wrap gap-2">
                {games.map((game) => (
                  <Badge
                    key={game.id}
                    variant={selectedGames.includes(game.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleGameSelection(game.id)}
                  >
                    {game.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Location Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>País</Label>
                <Select
                  value={selectedCountry.toString()}
                  onValueChange={(value) => {
                    setSelectedCountry(parseInt(value))
                    setSelectedRegion(0)
                    setSelectedLocation(0)
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos los países" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Todos los países</SelectItem>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id.toString()}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Región</Label>
                <Select
                  value={selectedRegion.toString()}
                  onValueChange={handleRegionChange}
                  disabled={!selectedCountry}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las regiones" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Todas las regiones</SelectItem>
                    {regions.map((region) => (
                      <SelectItem key={region.id} value={region.id.toString()}>
                        {region.fullName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Comuna</Label>
                <Select
                  value={selectedLocation.toString()}
                  onValueChange={(value) => setSelectedLocation(parseInt(value))}
                  disabled={!selectedRegion}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las comunas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Todas las comunas</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tournament Types Multi-select */}
            <div className="space-y-2">
              <Label>Tipos de Torneo</Label>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                {tournamentTypes.map((type) => (
                  <Badge
                    key={type.id}
                    variant={selectedTournamentTypes.includes(type.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleTournamentTypeSelection(type.id)}
                  >
                    {type.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Stores Select */}
            <div className="space-y-2">
              <Label>Tiendas</Label>
              <Select value="" onValueChange={handleStoreChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tienda..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Todas las tiendas</SelectItem>
                  {stores.map((store) => (
                    <SelectItem
                      key={store.id}
                      value={store.id.toString()}
                      disabled={selectedStores.includes(store.id)}
                    >
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStores.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedStores.map((storeId) => (
                    <Badge
                      key={storeId}
                      variant="default"
                      className="cursor-pointer"
                      onClick={() => removeStore(storeId)}
                    >
                      {storesMap.get(storeId) || `ID: ${storeId}`} ✕
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Search Button */}
            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSearch}
                disabled={usersLoading || tournamentsLoading}
                className="gap-2"
              >
                <Search className="h-4 w-4" />
                {usersLoading || tournamentsLoading ? "Buscando..." : "Buscar"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Toggle & Section */}
        {((activeReport === "users" && hasUsersData) ||
          (activeReport === "tournaments" && hasTournamentsData)) && (
          <div className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDashboard(!showDashboard)}
              className="gap-2 mb-4"
            >
              <BarChart3 className="h-4 w-4" />
              {showDashboard ? "Ocultar Dashboard" : "Ver Dashboard"}
            </Button>

            {showDashboard && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {activeReport === "users" && (
                  <>
                    {/* Users by Country */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Usuarios por País</CardTitle>
                        <CardDescription>Top 10 países con más usuarios</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={usersByCountryData} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis type="number" className="text-xs" />
                              <YAxis
                                dataKey="name"
                                type="category"
                                width={100}
                                className="text-xs"
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--background))",
                                  border: "1px solid hsl(var(--border))",
                                }}
                              />
                              <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Email Validity Pie */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Validación de Email</CardTitle>
                        <CardDescription>Distribución de emails válidos/inválidos</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={usersByEmailValidData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) =>
                                  `${name}: ${(percent * 100).toFixed(0)}%`
                                }
                                outerRadius={80}
                                dataKey="value"
                              >
                                {usersByEmailValidData.map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--background))",
                                  border: "1px solid hsl(var(--border))",
                                }}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Users Participation Trend */}
                    {usersTrendData.length > 0 && (
                      <Card className="lg:col-span-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">
                            Participación por Torneo
                          </CardTitle>
                          <CardDescription>
                            Cantidad de usuarios participantes por torneo
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={usersTrendData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="name" className="text-xs" />
                                <YAxis className="text-xs" />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "hsl(var(--background))",
                                    border: "1px solid hsl(var(--border))",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="usuarios"
                                  stroke={CHART_COLORS[5]}
                                  strokeWidth={2}
                                  dot={{ fill: CHART_COLORS[5], strokeWidth: 2 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}

                {activeReport === "tournaments" && (
                  <>
                    {/* Tournaments by Store */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Torneos por Tienda</CardTitle>
                        <CardDescription>Top 10 tiendas con más torneos</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={tournamentsByStoreData} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis type="number" className="text-xs" />
                              <YAxis
                                dataKey="name"
                                type="category"
                                width={120}
                                className="text-xs"
                              />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--background))",
                                  border: "1px solid hsl(var(--border))",
                                }}
                              />
                              <Bar dataKey="value" fill={CHART_COLORS[4]} radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tournaments by Game */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Torneos por Juego</CardTitle>
                        <CardDescription>Distribución por juego</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={tournamentsByGameData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) =>
                                  `${name}: ${(percent * 100).toFixed(0)}%`
                                }
                                outerRadius={80}
                                dataKey="value"
                              >
                                {tournamentsByGameData.map((_, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={CHART_COLORS[index % CHART_COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--background))",
                                  border: "1px solid hsl(var(--border))",
                                }}
                              />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tournaments by Status */}
                    <Card className="lg:col-span-2">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Torneos por Estado</CardTitle>
                        <CardDescription>Distribución por estado del torneo</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[200px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={tournamentsByStatusData}>
                              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                              <XAxis dataKey="name" className="text-xs" />
                              <YAxis className="text-xs" />
                              <Tooltip
                                contentStyle={{
                                  backgroundColor: "hsl(var(--background))",
                                  border: "1px solid hsl(var(--border))",
                                }}
                              />
                              <Bar dataKey="value" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Tournaments Trend */}
                    {tournamentsTrendData.length > 0 && (
                      <Card className="lg:col-span-2">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-sm font-medium">
                                Tendencia de Torneos
                              </CardTitle>
                              <CardDescription>Cantidad de torneos por período</CardDescription>
                            </div>
                            <Select
                              value={trendGranularity}
                              onValueChange={(v) =>
                                setTrendGranularity(v as "day" | "week" | "month")
                              }
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="day">Por Día</SelectItem>
                                <SelectItem value="week">Por Semana</SelectItem>
                                <SelectItem value="month">Por Mes</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={tournamentsTrendData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="name"
                                  className="text-xs"
                                  angle={-45}
                                  textAnchor="end"
                                  height={60}
                                />
                                <YAxis className="text-xs" />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "hsl(var(--background))",
                                    border: "1px solid hsl(var(--border))",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="torneos"
                                  stroke={CHART_COLORS[0]}
                                  strokeWidth={2}
                                  dot={{ fill: CHART_COLORS[0], strokeWidth: 2 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Participants Trend */}
                    {participantsTrendData.length > 0 && (
                      <Card className="lg:col-span-2">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">
                            Tendencia de Participantes
                          </CardTitle>
                          <CardDescription>
                            Cantidad de inscripciones por período (
                            {trendGranularity === "day"
                              ? "día"
                              : trendGranularity === "week"
                                ? "semana"
                                : "mes"}
                            )
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={participantsTrendData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis
                                  dataKey="name"
                                  className="text-xs"
                                  angle={-45}
                                  textAnchor="end"
                                  height={60}
                                />
                                <YAxis className="text-xs" />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "hsl(var(--background))",
                                    border: "1px solid hsl(var(--border))",
                                  }}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="participantes"
                                  stroke={CHART_COLORS[1]}
                                  strokeWidth={2}
                                  dot={{ fill: CHART_COLORS[1], strokeWidth: 2 }}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Results Section */}
        <TabsContent value="users" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg">Resultados - Usuarios Únicos</CardTitle>
                <CardDescription>
                  {persons.length > 0
                    ? `${filteredPersons.length} de ${persons.length} usuarios${usersSearchQuery ? " (filtrados)" : ""}`
                    : "Ejecute una búsqueda para ver resultados"}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {persons.length > 0 && (
                  <>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar en resultados..."
                        value={usersSearchQuery}
                        onChange={(e) => handleUsersSearchChange(e.target.value)}
                        className="pl-9 w-[200px]"
                      />
                      {usersSearchQuery && (
                        <button
                          onClick={() => handleUsersSearchChange("")}
                          className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button variant="outline" onClick={exportToExcel} className="gap-2">
                      <Download className="h-4 w-4" />
                      Exportar Excel
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {usersLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredPersons.length > 0 ? (
                <>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Código</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Documento</TableHead>
                          <TableHead>País</TableHead>
                          <TableHead>Región</TableHead>
                          <TableHead className="text-center">Torneos</TableHead>
                          <TableHead className="text-center">Email Válido</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedUsers.map((person: ReportPerson) => (
                          <TableRow key={person.id}>
                            <TableCell className="font-mono text-sm">{person.code}</TableCell>
                            <TableCell>
                              {person.name} {person.firstSurname} {person.lastSurname}
                            </TableCell>
                            <TableCell>{person.email}</TableCell>
                            <TableCell>
                              {person.documentNumber ? (
                                <span className="text-sm">
                                  <span className="text-muted-foreground">
                                    {documentTypeMap[person.documentNumberType || 0] || "Doc"}:
                                  </span>{" "}
                                  {person.documentNumber}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>{getCountryName(person.Address?.countryId)}</TableCell>
                            <TableCell>{getRegionName(person.Address?.regionId)}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">{person.playedTournaments}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={person.emailValid ? "default" : "destructive"}>
                                {person.emailValid ? "Sí" : "No"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {totalUsersPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Página {usersPage} de {totalUsersPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUsersPage((p) => Math.max(1, p - 1))}
                          disabled={usersPage === 1}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUsersPage((p) => Math.min(totalUsersPages, p + 1))}
                          disabled={usersPage === totalUsersPages}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : persons.length > 0 && usersSearchQuery ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No se encontraron resultados para "{usersSearchQuery}"</p>
                  <Button variant="link" onClick={() => handleUsersSearchChange("")}>
                    Limpiar búsqueda
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay datos para mostrar</p>
                  <p className="text-sm">Configure los filtros y presione buscar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tournaments" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-lg">Resultados - Torneos por Tiendas</CardTitle>
                <CardDescription>
                  {tournaments.length > 0
                    ? `${filteredTournaments.length} de ${tournaments.length} torneos${tournamentsSearchQuery ? " (filtrados)" : ""}`
                    : "Ejecute una búsqueda para ver resultados"}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {tournaments.length > 0 && (
                  <>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Buscar en resultados..."
                        value={tournamentsSearchQuery}
                        onChange={(e) => handleTournamentsSearchChange(e.target.value)}
                        className="pl-9 w-[200px]"
                      />
                      {tournamentsSearchQuery && (
                        <button
                          onClick={() => handleTournamentsSearchChange("")}
                          className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <Button variant="outline" onClick={exportToExcel} className="gap-2">
                      <Download className="h-4 w-4" />
                      Exportar Excel
                    </Button>
                  </>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {tournamentsLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredTournaments.length > 0 ? (
                <>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Nombre</TableHead>
                          <TableHead>Tienda</TableHead>
                          <TableHead>Juego</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Formato</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead className="text-center">Jugadores</TableHead>
                          <TableHead className="text-center">Estado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedTournaments.map((tournament: ReportTournament) => (
                          <TableRow key={tournament.id}>
                            <TableCell className="font-mono text-sm">{tournament.id}</TableCell>
                            <TableCell className="font-medium">{tournament.name}</TableCell>
                            <TableCell>{storesMap.get(tournament.storeId) || "-"}</TableCell>
                            <TableCell>{gamesMap.get(tournament.gameId)?.name || "-"}</TableCell>
                            <TableCell>
                              {tournamentTypesMap.get(tournament.typeId) || "-"}
                            </TableCell>
                            <TableCell>{formatsMap.get(tournament.formatId) || "-"}</TableCell>
                            <TableCell>
                              {tournament.startDate
                                ? format(new Date(tournament.startDate), "dd/MM/yyyy", {
                                    locale: es,
                                  })
                                : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="secondary">
                                {tournament.TournamentPlayers?.length || 0}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={
                                  tournament.statusId === 4
                                    ? "default"
                                    : tournament.statusId === 3
                                      ? "secondary"
                                      : "outline"
                                }
                              >
                                {tournament.statusId === 4
                                  ? "Finalizado"
                                  : tournament.statusId === 3
                                    ? "En curso"
                                    : tournament.statusId === 2
                                      ? "Inscripciones"
                                      : "Pendiente"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Pagination */}
                  {totalTournamentsPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Página {tournamentsPage} de {totalTournamentsPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setTournamentsPage((p) => Math.max(1, p - 1))}
                          disabled={tournamentsPage === 1}
                        >
                          Anterior
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setTournamentsPage((p) => Math.min(totalTournamentsPages, p + 1))
                          }
                          disabled={tournamentsPage === totalTournamentsPages}
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : tournaments.length > 0 && tournamentsSearchQuery ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No se encontraron resultados para "{tournamentsSearchQuery}"</p>
                  <Button variant="link" onClick={() => handleTournamentsSearchChange("")}>
                    Limpiar búsqueda
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No hay datos para mostrar</p>
                  <p className="text-sm">Configure los filtros y presione buscar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

const racesMap: Record<number, string> = {
  1: "Caballero",
  2: "Guerrero",
  3: "Sombra",
  4: "Bestia",
  5: "Dragón",
  6: "Eterno",
  7: "Faerie",
  8: "Olímpico",
  9: "Héroe",
  10: "Titán",
  11: "Desafiante",
  12: "Defensor",
  13: "Faraón",
  14: "Sacerdote",
  15: "Cazador",
  16: "Licántropo",
  17: "Vampiro",
  18: "Chamán",
  19: "Abominación",
  20: "Bárbaro",
  21: "Dios",
  22: "Campeón",
  23: "Criaturas",
  24: "Kami",
  25: "Xian",
  26: "Tótem",
  27: "Faerie / Goblin",
  28: "Titán / Minotauro",
  29: "Defensor / Druida",
  30: "Sombra / Drácula",
  31: "Triada Paladín",
  32: "Triada Tenebris",
  33: "Triada Desafiante",
  34: "Faraón / Guerrero",
  35: "Ancestral",
  36: "Desafiante / Sidhe",
}
